import { useMemo } from 'react';
import { DataTable, DataTableBody } from '@optiaxiom/react';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';

import { Tool } from '@frontend/types';

import createColumns from './columns';
import { ToolsTableRow } from './types';

type ToolsTableProps = {
  tools: Tool[];
  isUpdating: boolean;
  updatingToolId: string | null | undefined;
  isUpdatingEnabledInPrimaryAgent: boolean;
  updatingToolIdEnabledInPrimaryAgent: string | null | undefined;
  onToggleActive: (toolId: string, isDisabled: boolean) => void;
  onToggleEnabledInPrimaryAgent: (toolName: string, isEnabledInPrimaryAgent: boolean) => void;
};

export function ToolsTable({
  tools,
  isUpdating,
  updatingToolId,
  isUpdatingEnabledInPrimaryAgent,
  updatingToolIdEnabledInPrimaryAgent,
  onToggleActive,
  onToggleEnabledInPrimaryAgent,
}: ToolsTableProps) {
  const columns = useMemo(
    () =>
      createColumns({
        isUpdating,
        updatingToolId,
        isUpdatingEnabledInPrimaryAgent,
        updatingToolIdEnabledInPrimaryAgent,
        onToggleActive,
        onToggleEnabledInPrimaryAgent,
      }),
    [
      isUpdating,
      updatingToolId,
      isUpdatingEnabledInPrimaryAgent,
      updatingToolIdEnabledInPrimaryAgent,
      onToggleActive,
      onToggleEnabledInPrimaryAgent,
    ]
  );

  const data = useMemo(() => {
    const registryMap = new Map<string, ToolsTableRow>();
    tools.forEach(tool => {
      const registryId = tool.service.id;
      if (!registryMap.has(registryId)) {
        registryMap.set(registryId, {
          id: tool.service.id,
          name: tool.service.name,
          isRegistry: true,
          registry: tool.service,
          items: [
            {
              id: tool.function_id,
              name: tool.name,
              isRegistry: false,
              auth_status: tool.auth_status,
              tool: tool,
              items: [],
            },
          ],
        });
        return;
      }
      registryMap.get(registryId)?.items.push({
        id: tool.function_id,
        name: tool.name,
        isRegistry: false,
        auth_status: tool.auth_status,
        tool: tool,
        items: [],
      });
    });

    return Array.from(registryMap.values());
  }, [tools]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.items,
  });

  return (
    <DataTable table={table}>
      <DataTableBody />
    </DataTable>
  );
}
